<template>
    <div id="workstation-profiles-on">
        <div
            class="timer"
            v-if="timer && !timer.finished"
        >
            <product-timer
                :loading="loading"
                @finish="onFinish"
                @pause="onPause"
                @unpause="startTimer(true)"
                :saving="saving"
                :pausing-or-restarting="pausingOrRestarting"
                :timer="timer"
                :all-finished="allFinished"
            />
        </div>
        <div class="paint-work-form-entry">
            <ui5-label for="sawmachine-profile">
                {{ trans('pages.workstation_profiles_prep.scan_label') }}
            </ui5-label>
            <ui5-input
                type="Text"
                :disabled="loading || (timer && !timer.finished)"
                :value="paintWorkFormId"
                @input="paintWorkFormId = $event.target.value"
                @blur="loadData"
                @keyup.enter="loadData"
                :placeholder="
                    trans('pages.workstation_profiles_prep.scan_placeholder')
                "
            />
            <div class="camera-icon">
                <ui5-button
                    @click="scan"
                    :disabled="loading || (timer && !timer.finished)"
                >
                    <i class="fa fa-camera fa-2x" />
                </ui5-button>
            </div>
        </div>
        <div
            class="paint-work-form-view"
            v-if="paintWorkForm"
        >
            <pre>{{ paintWorkForm }}</pre>
        </div>
    </div>
</template>
<script>
import Spinner from '../components/loader/Spinner';
import productionTimesApi from '../api/productionTimes';
import paintWorkOrderApi from '../api/paintWorkOrder';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import workstationApi from '../api/workstation';
import ProductTimer from '../components/scanProducts/ProductTimer';
import { paintWorkFormMocker } from '../shared/qrMockers';
import { createAggregatedTimer } from '../shared/timers';
import localization from '../mixins/localization';

const defaultData = {
    pausingOrRestarting: false,
    timer: null,
    workstation: null,
    saving: false,
    loadingTimer: false,
    loadingPaintWorkForm: false,
    paintWorkFormId: '',
    paintWorkForm: null,
};
export default {
    mixins: [NavigationFunctionsMixin, localization],
    components: { Spinner, ProductTimer },
    data() {
        return {
            ...defaultData,
        };
    },
    mounted() {
        workstationApi
            .fetchByCustomView('#workstationprofilesprep')
            .then((response) => {
                this.workstation = response.data.data;
            });

        this.$bus.$on('barcode-scan', (data) => {
            this.paintWorkFormId = data;
            this.loadData();
        });

        this.$bus.$on('qr_scanner.on_scan', (key, value) => {
            if (key === 'workstationprofilesprep') {
                this.onScan(value);
            }
        });
    },
    computed: {
        Config: () => Config,
        loading() {
            return this.loadingPaintWorkForm || this.loadingTimer;
        },
        allFinished() {
            return true;
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.timer && !this.timer.finished) {
            this.onFinish();
        }
        next();
    },
    methods: {
        finish(pause) {
            const recordIds = this.timer.products.map(
                (x) => x.historyRecord.ID
            );
            return productionTimesApi
                .finishPaintWorkOrder(this.paintWorkFormId, recordIds, pause)
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_prep.errors.unknown_save_error'
                        )
                    );
                    this.timer.finished = false;
                });
        },
        onFinish() {
            this.saving = true;
            this.finish(false)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.workstation_profiles_prep.success.production_time_saved'
                        )
                    );
                    Object.keys(defaultData).forEach((key) => {
                        this[key] = JSON.parse(
                            JSON.stringify(defaultData[key])
                        );
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onPause() {
            this.pausingOrRestarting = true;
            this.finish(true)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.workstation_profiles_prep.success.production_time_saved'
                        )
                    );
                })
                .finally(() => {
                    this.pausingOrRestarting = false;
                });
        },
        startTimer(restart = false) {
            if (restart) {
                this.pausingOrRestarting = true;
            } else {
                this.loadingTimer = true;
            }
            productionTimesApi
                .fetchPaintWorkOrder(this.paintWorkFormId, this.workstation.id)
                .then((response) => {
                    const timerData = response.data.data;
                    if (timerData.missingMinutes.length > 0) {
                        const str = timerData.missingMinutes
                            .map((x) => '#' + x)
                            .join(', ');
                        const plural =
                            timerData.missingMinutes.length == 1 ? '' : 's';
                        showInfoMessage(
                            this.trans(
                                'pages.workstation_profiles_prep.info.missing_target_times',
                                {
                                    plural: plural,
                                    types: str,
                                }
                            )
                        );
                    }
                    this.timer = createAggregatedTimer(timerData);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_prep.errors.timer_start_error'
                        )
                    );
                })
                .finally(() => {
                    if (restart) {
                        this.pausingOrRestarting = false;
                    } else {
                        this.loadingTimer = false;
                    }
                });
        },
        scan() {
            this.$bus.$emit('qr_scanner.start', 'workstationprofilesprep', {
                singleScan: true,
                defaultCamera: 'front',
                mocker: paintWorkFormMocker,
            });
        },
        onScan(value) {
            let decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {}

            if (decoded && decoded.context === 'paintworkform') {
                this.paintWorkFormId = decoded.paintworkformId;
                this.loadData();
            } else {
                showErrorMessage(
                    this.trans(
                        'pages.workstation_profiles_prep.errors.invalid_qr_code'
                    ),
                    null,
                    1500
                );
            }
        },
        loadData() {
            if (this.loadingPaintWorkForm) {
                return;
            }

            if (this.paintWorkFormId.toString().length == 0) {
                return showErrorMessage(
                    this.trans(
                        'pages.workstation_profiles_prep.errors.invalid_paint_work_form_number'
                    )
                );
            }

            this.loadingPaintWorkForm = true;

            paintWorkOrderApi
                .detail(this.paintWorkFormId)
                .then((response) => {
                    const paintWorkForm = response.data.data;
                    this.paintWorkFormId =
                        paintWorkForm.paintworkorder.paintworkorderId;
                    this.paintWorkForm = paintWorkForm;
                    this.startTimer();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_prep.errors.paint_work_form_load_error'
                        )
                    );
                })
                .finally(() => {
                    this.loadingPaintWorkForm = false;
                });
        },
    },
};
</script>
